export type FieldType = 'text' | 'number' | 'boolean' | 'date' | 'url';
export type OntologyLayer = 'business' | 'metric' | 'data' | 'operation';
export type RelationKind = 'semantic' | 'calculation' | 'lineage' | 'influence' | 'causal' | 'ownership';
export type RelationPolarity = 'positive' | 'negative' | 'mixed' | 'unknown';
export type EvidenceLevel = 'declared' | 'observed' | 'validated';
export type PageView = 'overview' | 'metrics' | 'lineage' | 'impact' | 'operations' | 'models';

export interface OntologyField {
  id: string;
  key: string;
  label: string;
  dataType: FieldType;
  required: boolean;
}

export interface OntologyType {
  id: string;
  key: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  layer: OntologyLayer;
  system: boolean;
  fields: OntologyField[];
  entityCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface OntologyEntity {
  id: string;
  typeId: string;
  name: string;
  description: string;
  status: string;
  properties: Record<string, unknown>;
  createdBy?: string;
  createdAt: number;
  updatedAt: number;
}

export interface OntologyRelation {
  id: string;
  sourceEntityId: string;
  targetEntityId: string;
  label: string;
  description: string;
  kind: RelationKind;
  confidence: number;
  polarity: RelationPolarity;
  lag: string;
  condition: string;
  formula: string;
  evidenceLevel: EvidenceLevel;
  createdBy?: string;
  createdAt: number;
  updatedAt: number;
}

export interface OntologyEvidence {
  id: string;
  entityId: string;
  documentId: string;
  documentTitle: string;
  excerpt: string;
  createdBy?: string;
  createdAt: number;
}

export interface RelationEvidence {
  id: string;
  relationId: string;
  documentId: string;
  documentTitle: string;
  excerpt: string;
  createdBy?: string;
  createdAt: number;
}

export interface MetricObservation {
  id: string;
  metricEntityId: string;
  value: number;
  period: string;
  dimensions: Record<string, string>;
  source: string;
  observedAt: number;
  createdBy?: string;
  createdAt: number;
}

export interface OntologyStats {
  typeCount: number;
  entityCount: number;
  relationCount: number;
  evidenceCount: number;
  observationCount: number;
  metricCount: number;
  mappedMetricCount: number;
  orphanEntityCount: number;
  layerCounts: Record<OntologyLayer, number>;
  relationKindCounts: Record<RelationKind, number>;
}

export interface Snapshot {
  types: OntologyType[];
  entities: OntologyEntity[];
  relations: OntologyRelation[];
  evidence: OntologyEvidence[];
  relationEvidence: RelationEvidence[];
  observations: MetricObservation[];
  stats: OntologyStats;
}

export interface KnowledgeDocument {
  id: string;
  title: string;
  path: string;
  updatedAt: string;
}

export interface ImpactNode {
  entity: OntologyEntity;
  type: OntologyType;
  depth: number;
  relation: OntologyRelation;
  pathEntityIds: string[];
}

export interface ImpactAnalysis {
  focus: { entity: OntologyEntity; type: OntologyType };
  upstream: ImpactNode[];
  downstream: ImpactNode[];
  latestObservation?: MetricObservation;
  previousObservation?: MetricObservation;
  change?: { absolute: number; percent?: number };
  warnings: string[];
}

export const LAYER_META: Record<OntologyLayer, { label: string; short: string; description: string; color: string }> = {
  business: { label: '业务概念', short: '业务', description: '对象、事件、流程与经营目标', color: '#c54a2f' },
  metric: { label: '指标语义', short: '指标', description: '指标、维度、口径与观测值', color: '#5a8ab8' },
  data: { label: '数据血缘', short: '数据', description: '数据源、数据集与字段映射', color: '#5a6a2a' },
  operation: { label: '组织执行', short: '执行', description: '团队、Agent、项目与任务', color: '#8a6ac8' },
};

export const RELATION_META: Record<RelationKind, { label: string; description: string; color: string; dashed?: boolean }> = {
  semantic: { label: '语义关联', description: '业务对象之间的结构关系', color: '#8a6ac8', dashed: true },
  calculation: { label: '计算依赖', description: '公式或聚合上的确定依赖', color: '#5a8ab8' },
  lineage: { label: '数据血缘', description: '数据从哪里流转而来', color: '#5a6a2a' },
  influence: { label: '业务影响', description: '有方向的影响假设或经验关系', color: '#c79a3a', dashed: true },
  causal: { label: '因果关系', description: '经过证据支持或验证的原因关系', color: '#c54a2f' },
  ownership: { label: '责任关系', description: '谁负责、维护或执行', color: '#1b1b14', dashed: true },
};

export const EMPTY_SNAPSHOT: Snapshot = {
  types: [],
  entities: [],
  relations: [],
  evidence: [],
  relationEvidence: [],
  observations: [],
  stats: {
    typeCount: 0,
    entityCount: 0,
    relationCount: 0,
    evidenceCount: 0,
    observationCount: 0,
    metricCount: 0,
    mappedMetricCount: 0,
    orphanEntityCount: 0,
    layerCounts: { business: 0, metric: 0, data: 0, operation: 0 },
    relationKindCounts: { semantic: 0, calculation: 0, lineage: 0, influence: 0, causal: 0, ownership: 0 },
  },
};
